import { ApolloClient, createHttpLink, fromPromise, InMemoryCache } from "@apollo/client/core"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { devStaffSessionHeaders } from "@auger/identity"
import { isPageUnloading } from "@auger/ui/utils/pageLifecycle"
import { bearerHeader } from "@/common/authorization"
import config from "@/common/config"
import auth from "@/identity/auth"

// The Hasura role of the dev auth bypass. The user store reads it as the
// bypass session's only role, so the UI and Hasura agree on what it may do.
export const DEV_BYPASS_ROLE = "heavybit_staff"

// Local dev with sign-in off: the admin secret plus a staff session for the
// forced user. VITE_FORCE_PERSON is that user's person record; the owner
// rules compare it, so without it Hasura refuses every record query.
const headers: Record<string, string> = {}
if (config.auth.disabled && config.api.adminSecret && config.general.forcedUser) {
  headers["x-hasura-admin-secret"] = config.api.adminSecret
  Object.assign(
    headers,
    devStaffSessionHeaders(config.general.forcedUser, config.general.forcedPerson)
  )
}

const hasuraLink = createHttpLink({
  uri: config.api.url,
})

const hasuraContext = setContext(async (_, { headers: contextHeaders }) => {
  return { headers: { ...headers, ...contextHeaders, authorization: await bearerHeader() } }
})

export const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const op = operation.operationName
  const jwtError = graphQLErrors?.find((e) => e.extensions?.code === "invalid-jwt")
  // Assumes Hasura returns invalid-jwt alone (not mixed with other errors in
  // the same response) — true in practice for this failure mode. If that
  // ever changes, a co-occurring non-jwt error here would go unlogged.

  if (jwtError) {
    // forceRefreshToken() warms the Stytch SDK's cache; hasuraContext (the
    // next link) always recomputes the authorization header fresh via
    // bearerHeader(), so there's nothing to hand off here — just retry.
    // For a query/mutation over this HTTP link (single response, then
    // complete), Apollo's onError only invokes this handler once per
    // request — the retried observable's result is piped straight to the
    // caller, not back through this handler — so there's no infinite-loop
    // risk to guard against here. (A multi-emission link ahead of errorLink,
    // e.g. a future subscription transport, would need re-checking.) A
    // still-failing retry must still be logged explicitly below, or it
    // vanishes silently.
    return fromPromise(
      auth.forceRefreshToken().catch((error) => {
        console.error(
          `[GraphQL Error] ${op}: forced refresh failed while recovering from invalid-jwt`,
          {
            error,
            variables: operation.variables,
          }
        )
        throw error
      })
    ).flatMap(() =>
      forward(operation).map((result) => {
        if (result.errors?.some((e) => e.extensions?.code === "invalid-jwt")) {
          console.error(`[GraphQL Error] ${op}: still invalid-jwt after a forced refresh`, {
            errors: result.errors,
            variables: operation.variables,
          })
        }
        return result
      })
    )
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(`[GraphQL Error] ${op}: ${message}`, {
        variables: operation.variables,
        path,
        locations,
        extensions,
      })
    })
  }

  if (networkError && !isPageUnloading()) {
    console.error(`[Network Error] ${op}: ${networkError}`, {
      variables: operation.variables,
    })
  }

  if (operation.query && Object.keys(operation.query).length === 0) {
    console.error(
      `[Invalid Query] ${op}: query is an empty object — check for string interpolation in graphql() calls`,
      { variables: operation.variables }
    )
  }
  // Logged; the errors reach the caller unchanged.
  return
})

export const client = new ApolloClient({
  link: errorLink.concat(hasuraContext).concat(hasuraLink),
  cache: new InMemoryCache(),
  devtools: { enabled: import.meta.env.DEV },
})
